import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AnimateIn from "@/components/AnimateIn";
import ProductCard from "@/components/ProductCard";
import { useShop } from "@/context/ShopContext";

const TYPES = ["all", "hoodies", "tees", "shorts", "bundles"];
const SORTS = [
  { id: "featured", label: "Featured" },
  { id: "newest", label: "Newest" },
  { id: "low", label: "Price: low to high" },
  { id: "high", label: "Price: high to low" },
];

function productMatchesType(p, type) {
  if (type === "all") return true;
  const hay = [p.type, ...(p.tags || [])].join(" ").toLowerCase();
  return hay.includes(type);
}

// For "Featured" sort — group tees first, then hoodies, then everything else.
// Shopify's default order can intermix categories; this keeps the shop tidy.
function categoryRank(p) {
  const tags = (p.tags || []).map((t) => t.toLowerCase());
  if (tags.includes("tees")) return 0;
  if (tags.includes("hoodies")) return 1;
  if (tags.includes("shorts")) return 2;
  if (tags.includes("bundles")) return 3;
  return 9;
}

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const type = params.get("type") || "all";
  const [sort, setSort] = useState("featured");
  const { products, loading } = useShop();

  const filtered = useMemo(() => {
    let list = products.filter((p) => productMatchesType(p, type));
    if (sort === "featured") list = [...list].sort((a, b) => categoryRank(a) - categoryRank(b));
    if (sort === "low") list.sort((a, b) => a.price - b.price);
    if (sort === "high") list.sort((a, b) => b.price - a.price);
    return list;
  }, [type, sort, products]);

  return (
    <div data-testid="shop-page">
      <section className="bg-black pt-[160px] md:pt-[200px] pb-16 px-6 md:px-12 text-center">
        <AnimateIn variant="fade">
          <h1 className="wc-heading text-white text-5xl md:text-7xl tracking-widest">
            THE COLLECTION
          </h1>
        </AnimateIn>
        <AnimateIn variant="fade" delay={150}>
          <p className="text-white/70 mt-5 text-base md:text-lg">
            Core collection always available. Drops are limited.
          </p>
        </AnimateIn>
      </section>

      <section
        className="bg-black px-6 md:px-12 border-y border-white/10 py-5"
        data-testid="shop-filter-bar"
      >
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-4">
          <div className="flex flex-wrap gap-6">
            {TYPES.map((t) => {
              const active = t === type;
              return (
                <button
                  key={t}
                  onClick={() => setParams(t === "all" ? {} : { type: t })}
                  className={`wc-label text-xs pb-1 border-b transition-colors ${
                    active ? "text-white border-white" : "text-white/50 border-transparent"
                  }`}
                  data-testid={`shop-filter-${t}`}
                >
                  {t.toUpperCase()}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-3">
            <span className="wc-label text-xs text-white/50">SORT</span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-transparent border border-white/30 text-white wc-label text-xs px-3 py-2 outline-none"
              data-testid="shop-sort"
            >
              {SORTS.map((s) => (
                <option key={s.id} value={s.id} className="bg-black">
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <section className="bg-black px-6 md:px-12 py-20" data-testid="shop-grid">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <p className="text-white/40 text-center wc-label text-xs">LOADING COLLECTION…</p>
          ) : filtered.length === 0 ? (
            <p className="text-white/50 text-center">No products in this category yet.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-10">
              {filtered.map((p, i) => (
                <AnimateIn key={p.id || p.handle} variant="fade" delay={i * 80}>
                  <ProductCard product={p} />
                </AnimateIn>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
